import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import TicketsNotAvailableText from "@app-contexts/public/features/tickets/components/TicketsNotAvailableText/TicketsNotAvailableText.web";
import TicketTypesContainer from "@app-contexts/public/features/tickets/components/TicketTypesContainer/TicketTypesContainer.web";
import ItemQuantitySelectInfo from "@shared/components/application/data-display/ItemQuantitySelectInfo/ItemQuantitySelectInfo.web";
import DetailCardContainer from "@shared/components/ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { displayTicketType } from "@shared/modules/events/utils/detail-page-helpers";
import {
  eventHasWaitlistItems,
  isEventWithoutUpcomingTicketSales,
  isPastEvent,
  isPublicEvent,
  isSoldOutEvent,
  isVenueBasedEvent,
} from "@shared/modules/events/utils/event-utils";
import { cleanTicketType } from "@shared/modules/events/utils/ticket-type-utils";
import { useModelService } from "@shared/services/api/hooks/useModelService";
import url from "@shared/utils/urls/url";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { AnalyticsService } from "@shared/services/analytics/AnalyticsService";

import BackButton from "@shared/components/application/form/BackButton/BackButton.web";
import { useSubOptionsOptional } from "@shared/contexts/SubOptionsContext/SubOptionsContext";
import RecurringTimeslotCards from "../../RecurringTimeslotCards/RecurringTimeslotCards.web";
import TicketTypePatronItemsContainer from "../patron-items/TicketTypePatronItemsContainer/TicketTypePatronItemsContainer.web";
import {
  TicketListComponentProps,
  TicketTypesListProps,
} from "./TicketTypesList.props";

const TicketListComponent = ({
  inputEvent,
  isLoadingEvent,
  initialSelectedTicketTypes,
  onAmountRequested,
  basketVoucherCode,
  holdDetailsData,
  showItemIds,
  ticketsAuthorizedByPassword,
  userVouchers,
  pastEvent,
  soldOutEvent,
  ticketsNotRequired,
  hasWaitlistItems,
  parentEvent,
  forceQuantityButtons = false,
  withDebounce = true,
  enableTracking = true,
  enableAffirm = true,
  onCustomPackageSelect,
  venueData,
}: TicketListComponentProps) => {
  const { t } = useTranslation("common");

  // Use sub-options context if available (includes centralized basket logic)
  const subOptionsContext = useSubOptionsOptional();

  // array of authorized password-protected tickets
  // does not persist between page refreshes
  const [ticketAuthArray, setTicketAuthArray] = useState<
    Array<{ id: number; authorized: boolean }>
  >([]);

  const updateTicketAuthArray = (
    input: Array<{ id: number; authorized: boolean }>
  ) => {
    setTicketAuthArray(input);
  };

  let ticketTypes: TicketTypeSerializer[] = [];

  if (inputEvent && isVenueBasedEvent(inputEvent)) {
    ticketTypes = inputEvent.tickettype_set ?? [];
  } else if (inputEvent && isPublicEvent(inputEvent)) {
    ticketTypes = inputEvent?.ticket_types ?? [];
  }

  // Always filter out sub-option child tickets from main ticket types
  const mainTicketTypes = useMemo(() => {
    return ticketTypes.filter(
      (tt: TicketTypeSerializer) => !tt.is_sub_option_child
    );
  }, [ticketTypes]);

  // Get centralized sub-option data and functions from context
  const filteredTicketTypes: TicketTypeSerializer[] =
    subOptionsContext?.getFilteredTicketTypes(mainTicketTypes) ||
    mainTicketTypes;

  // Helper function to get purchase limits (combines sub-options and hold logic)
  const getPurchaseLimits = (ticketId: number) => {
    // First check for sub-option limits from context
    const subOptionLimits =
      subOptionsContext?.getSubOptionPurchaseLimits(ticketId);
    if (subOptionLimits) {
      return subOptionLimits;
    }

    // Then check for hold limits (component-specific logic)
    if (holdDetailsData) {
      return {
        min: holdDetailsData?.on_hold_detail?.purchase_limit_min ?? 0,
        max: holdDetailsData?.on_hold_detail?.purchase_limit_max ?? 0,
      };
    }

    return undefined;
  };

  // Note: hasSubOptionChildren detection and auto-hide logic is now handled in SubOptionsContext

  const ticketTypesWithPatronType = useMemo(() => {
    return filteredTicketTypes.filter((tt: TicketTypeSerializer) =>
      Boolean(tt.patron_type)
    );
  }, [filteredTicketTypes]);

  const ticketTypesWithoutPatronType = useMemo(() => {
    return filteredTicketTypes.filter(
      (tt: TicketTypeSerializer) => !tt.patron_type
    );
  }, [filteredTicketTypes]);

  if (!inputEvent) {
    return null;
  }

  const { venue } = inputEvent;
  const recurringEvent = parentEvent?.is_recurring_parent;

  const getDynamicHoldEvent = () => {
    return holdDetailsData?.events?.find((ev) => ev.id === inputEvent.id);
  };

  const isDynamicHoldItem = (tt: TicketTypeSerializer): boolean => {
    if (!tt || !holdDetailsData) return false;
    return Boolean(
      getDynamicHoldEvent()?.ticket_types?.some((t) => t.id === tt.id)
    );
  };

  return (
    // TODO: Use mt="none" instead of css prop
    // eslint-disable-next-line showpass-custom/prefer-semantic-props
    <Flex w="full" direction="column" gap="md" css={{ marginTop: 0 }}>
      {/* Sub-options header with back button */}
      {subOptionsContext?.isViewingSubOptions && (
        <Flex direction="column" gap="md" mb="xl">
          <BackButton
            data-testid="back-to-main-tickets"
            onClick={subOptionsContext.hideSubOptions}
          />
          <Box>
            <Text fontSize="lg" fontWeight="semibold">
              {t("Add-ons")}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {t("Select optional add-ons for your experience")}
            </Text>
          </Box>
        </Flex>
      )}

      {Boolean(ticketTypesWithPatronType?.length) &&
        !subOptionsContext?.isViewingSubOptions && (
          <TicketTypePatronItemsContainer
            initialSelectedTicketTypes={initialSelectedTicketTypes}
            onAmountRequested={onAmountRequested}
            ticketTypes={ticketTypes ?? []}
            event={inputEvent}
            basketVoucherCode={basketVoucherCode as string | undefined}
            holdDetailsData={holdDetailsData}
            ticketsAuthorizedByPassword={ticketsAuthorizedByPassword ?? []}
            ticketAuthArray={ticketAuthArray}
            updateTicketAuthArray={updateTicketAuthArray}
          />
        )}
      {ticketTypesWithoutPatronType
        // if set only show specific ticket types for attraction pages;
        // an empty array means "no constraint" — show everything (attraction
        // config often stores `item_ids: []` for parent events where the whole
        // ticket-type list should be shown)
        ?.filter(
          (tt: TicketTypeSerializer) =>
            !showItemIds?.length || showItemIds.includes(tt.id)
        )
        ?.sort((a, b) =>
          Number(a?.sort_order) < Number(b?.sort_order) ? -1 : 1
        )
        .map((tt: TicketTypeSerializer) => {
          // if viewing hold, then only show items from the hold
          const shouldShowForHold =
            !!holdDetailsData === !!isDynamicHoldItem(tt);

          // Check if ticket type should be displayed (password protection, sale timing, etc.)
          const shouldDisplayTicketType = displayTicketType(
            tt,
            inputEvent?.is_password_protected,
            ticketsAuthorizedByPassword
          );

          // Only render the Box wrapper if both conditions are met
          if (!shouldShowForHold || !shouldDisplayTicketType) {
            return null;
          }

          return (
            <Box
              key={tt.id}
              backgroundColor="bg"
              borderRadius="md"
              padding="md"
              // TODO: Use conditional rendering instead of CSS empty selector
              // eslint-disable-next-line showpass-custom/prefer-semantic-props
              css={{
                "&:empty": {
                  display: "none",
                },
              }}
            >
              <TicketTypesContainer
                initialAmountRequested={
                  initialSelectedTicketTypes?.[`${tt.id}`]
                }
                tt={cleanTicketType(tt, isDynamicHoldItem(tt))} // if viewing hold, then show sold out items
                eventData={inputEvent}
                venue={venueData ?? venue}
                isRecurring={Boolean(recurringEvent)}
                isLoading={isLoadingEvent}
                ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
                ticketAuthArray={ticketAuthArray}
                updateTicketAuthArray={updateTicketAuthArray}
                basketVoucherCode={basketVoucherCode}
                userVouchers={userVouchers}
                dynamicHoldSlug={
                  holdDetailsData?.on_hold_detail?.tracking_link_slug
                }
                purchaseLimits={getPurchaseLimits(tt.id)}
                onAmountRequested={onAmountRequested}
                forceQuantityButtons={forceQuantityButtons}
                withDebounce={withDebounce}
                enableTracking={enableTracking}
                enableAffirm={enableAffirm}
                onCustomPackageSelect={onCustomPackageSelect}
              />
            </Box>
          );
        })}

      {isEventWithoutUpcomingTicketSales(inputEvent) &&
        !subOptionsContext?.isViewingSubOptions && (
          <TicketsNotAvailableText
            ticketsNotRequired={Boolean(ticketsNotRequired)}
            pastEvent={pastEvent}
            soldOutEvent={soldOutEvent}
            waitlistedEvent={hasWaitlistItems}
            // parent event has this verbiage, so use eventData instead of inputEvent
            customTicketMessage={parentEvent?.no_ticket_types_message ?? ""}
          />
        )}
    </Flex>
  );
};

const TicketTypesList = ({
  eventData: initialEventData = null,
  isLoading: initialLoading,
  childEvent,
  hideBackButton,
  ticketsAuthorizedByPassword,
  basketVoucherCode,
  userVouchers,
  holdDetailsData = undefined,
  eventId,
  showItemIds,
  initialSelectedTicketTypes,
  numForSkeleton = 1,
  hideRecurringTimeslotTitle = false,
  onAmountRequested,
  onChildEventSelected,
  forceQuantityButtons = false,
  withDebounce = true,
  enableTracking = true,
  enableAffirm = true,
  onCustomPackageSelect,
  venueData,
}: TicketTypesListProps) => {
  const router = useRouter();
  const { useRetrieve } = useModelService(new PublicBaseEventRepository());
  const { data: retrievedEventData, isLoading: isRetrieveLoading } =
    useRetrieve(eventId, {
      config: {
        params: {
          payment_type: [
            PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
            PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
            PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
          ].toString(),
        },
      },
      options: {
        enabled: !initialEventData && !!eventId,
      },
    });

  const eventData = initialEventData || retrievedEventData;
  const isLoading = eventData
    ? false
    : Boolean(initialLoading) || isRetrieveLoading;

  // use for selected timeslots on recurring events
  const [selectedChildEvent, setSelectedChildEvent] = useState<
    PublicBaseEventSerializer | undefined
  >(undefined);
  const [isFetchingChildEvent, setIsFetchingChildEvent] = useState(false);

  let ticketTypes: TicketTypeSerializer[] = [];
  if (isPublicEvent(eventData)) {
    ticketTypes = eventData?.ticket_types ?? [];
  } else if (isVenueBasedEvent(eventData)) {
    ticketTypes = eventData?.tickettype_set ?? [];
  }

  useEffect(() => {
    if (childEvent) {
      setSelectedChildEvent(childEvent);
    }
  }, [childEvent]);

  const hasTicketTypes = ticketTypes && ticketTypes.length > 0;
  const hasChildEvents =
    eventData?.child_events && eventData?.child_events?.length > 0;
  const recurringEvent = eventData?.is_recurring_parent;

  const soldOutEvent = Boolean(eventData) && isSoldOutEvent(eventData);
  const hasWaitlistItems =
    Boolean(eventData) && eventHasWaitlistItems(eventData);

  const pastEvent = Boolean(eventData) && isPastEvent(eventData);
  const ticketsNotRequired = eventData?.no_ticket_types;

  const displayTicketsNotAvailableText =
    ticketsNotRequired || // event is free
    pastEvent || // event has ended
    (!recurringEvent && !hasTicketTypes) || // normal event with no ticket types
    (recurringEvent && !hasChildEvents); // recurring event with no child events

  // Track analytics when ticket types are displayed on event detail pages
  // Tracks for single events, not recurring with child tickets
  // We wait until the actual ticket types are displayed to track the viewTicketTypes events
  useEffect(() => {
    if (
      eventData &&
      ticketTypes &&
      ticketTypes.length > 0 &&
      !isLoading &&
      eventData.venue?.id &&
      !recurringEvent
    ) {
      // Track Google view_item (internal analytics + Google Analytics/GTM)
      AnalyticsService.viewTicketTypes(
        {
          ...eventData,
          name: eventData.name || "Unknown Event",
        },
        eventData.venue
      );
    }
  }, [eventData?.id, ticketTypes?.length, isLoading, recurringEvent]);
  /**
   * Redirects user to the custom package selection page.
   * Parent ticket id, quantity, and original event slug are query params hidden from the url.
   */
  const redirectToCustomPackagePage = (
    ticketTypeId: number,
    quantity: number,
    eventUrl?: string
  ) => {
    const currentUrl = url();
    const ticketPackageRoute = [eventUrl ?? currentUrl, "ticket-package/"].join(
      ""
    );

    // redirect to the ticket package route
    // router.push is used to hide the query params in the route
    router.push({
      pathname: ticketPackageRoute,
      query: {
        ticketType: ticketTypeId,
        ticketQuantity: quantity,
      },
    });
  };

  const handleCustomPackageSelect = (
    ticketTypeId: number,
    quantity: number,
    eventUrl?: string
  ) => {
    if (onCustomPackageSelect) {
      onCustomPackageSelect(ticketTypeId, quantity);
    } else {
      redirectToCustomPackagePage(ticketTypeId, quantity, eventUrl);
    }
  };

  return (
    <>
      {/* Display a loading skeleton */}
      {isLoading && (
        <Flex mt="md" flex="1" direction="column" h="100%">
          {Array.from({ length: numForSkeleton }, (_, i) => (
            <TicketTypesList.Skeleton key={i} />
          ))}
        </Flex>
      )}

      {/* Display recurring event selector and ticket types for each timeslot */}
      {recurringEvent && hasChildEvents && (
        <Flex direction="column" w="full" gap="md">
          <Flex w="full" direction="column">
            <RecurringTimeslotCards
              hideBackButton={hideBackButton}
              eventData={eventData}
              isFetchingChildEvent={isFetchingChildEvent}
              selectedChildEvent={selectedChildEvent}
              setSelectedChildEvent={setSelectedChildEvent}
              setIsFetchingChildEvent={setIsFetchingChildEvent}
              onChildEventSelected={onChildEventSelected}
              hideTitle={hideRecurringTimeslotTitle}
            />
          </Flex>

          {isFetchingChildEvent ? (
            <TicketTypesList.Skeleton />
          ) : (
            <TicketListComponent
              enableAffirm={enableAffirm}
              inputEvent={selectedChildEvent}
              isLoadingEvent={isFetchingChildEvent}
              hasWaitlistItems={hasWaitlistItems}
              soldOutEvent={soldOutEvent}
              pastEvent={pastEvent}
              ticketsNotRequired={Boolean(ticketsNotRequired)}
              basketVoucherCode={basketVoucherCode}
              holdDetailsData={holdDetailsData}
              venueData={venueData}
              initialSelectedTicketTypes={initialSelectedTicketTypes}
              onAmountRequested={onAmountRequested}
              parentEvent={eventData}
              showItemIds={showItemIds}
              ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
              userVouchers={userVouchers}
              forceQuantityButtons={forceQuantityButtons}
              withDebounce={withDebounce}
              enableTracking={enableTracking}
              hideStartEndLabels={false}
              onCustomPackageSelect={(ticketTypeId, quantity) => {
                handleCustomPackageSelect(
                  ticketTypeId,
                  quantity,
                  selectedChildEvent?.frontend_details_url
                );
              }}
            />
          )}
        </Flex>
      )}

      {/* Display non-recurring event ticket types */}
      {!recurringEvent && !pastEvent && hasTicketTypes && !isLoading && (
        <TicketListComponent
          enableAffirm={enableAffirm}
          inputEvent={eventData}
          parentEvent={eventData}
          isLoadingEvent={isLoading}
          hasWaitlistItems={hasWaitlistItems}
          soldOutEvent={soldOutEvent}
          pastEvent={pastEvent}
          ticketsNotRequired={Boolean(ticketsNotRequired)}
          basketVoucherCode={basketVoucherCode}
          holdDetailsData={holdDetailsData}
          initialSelectedTicketTypes={initialSelectedTicketTypes}
          onAmountRequested={onAmountRequested}
          showItemIds={showItemIds}
          ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
          userVouchers={userVouchers}
          venueData={venueData}
          forceQuantityButtons={forceQuantityButtons}
          withDebounce={withDebounce}
          hideStartEndLabels={false}
          onCustomPackageSelect={(ticketTypeId, quantity) => {
            handleCustomPackageSelect(
              ticketTypeId,
              quantity,
              eventData.frontend_details_url
            );
          }}
        />
      )}

      {/* Display not available text for all conditions except a sold out event */}
      {/* This prevents the text and sold out ticket cards from being shown together */}
      {displayTicketsNotAvailableText && !isLoading && (
        <TicketsNotAvailableText
          pastEvent={pastEvent}
          soldOutEvent={false}
          waitlistedEvent={false}
          ticketsNotRequired={Boolean(ticketsNotRequired)}
          customTicketMessage={eventData?.no_ticket_types_message ?? ""}
        />
      )}
    </>
  );
};

TicketTypesList.Skeleton = function TicketTypesListSkeleton(): JSX.Element {
  return (
    <DetailCardContainer>
      <ItemQuantitySelectInfo
        currency={enums.CURRENCY_MAP.CAD.id}
        isLoading
        amountSelected={0}
        onAmountSelectedChange={undefined}
        isWaitlistTicket
      />
    </DetailCardContainer>
  );
};
export default TicketTypesList;
